import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box
} from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * AddToCollectionDialog Component
 * 
 * A dialog for selecting which collection to add a case to.
 * Shows a dropdown with available collections and handles the selection.
 */
const AddToCollectionDialog = ({
  open,
  onClose,
  onConfirm,
  onCancel,
  collections = [],
  caseTitle = '',
  primaryColor = '#5757b0',
  lightPurple = '#f0f0ff'
}) => {
  const { t } = useTranslation();
  const [selectedCollectionId, setSelectedCollectionId] = useState('');

  const handleSubmit = () => {
    if (selectedCollectionId) {
      onConfirm(selectedCollectionId);
      setSelectedCollectionId(''); // Reset for next time
    }
  };

  const handleCancel = () => {
    setSelectedCollectionId(''); // Reset for next time
    onCancel();
  };

  const handleClose = () => {
    setSelectedCollectionId(''); // Reset for next time
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(87, 87, 176, 0.15)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        fontSize: '1.25rem',
        fontWeight: 600,
        color: primaryColor
      }}>
        {t('collection.add_to_collection_title') || 'Add to Collection'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('collection.add_case_to_collection_message') || 'Select a collection to add this case to:'}
          </Typography>
          {caseTitle && (
            <Typography variant="body1" sx={{ 
              mt: 1, 
              fontWeight: 500,
              color: primaryColor
            }}>
              "{caseTitle}"
            </Typography>
          )}
        </Box>

        <FormControl fullWidth sx={{ mt: 2 }}>
          <Select
            value={selectedCollectionId}
            onChange={(e) => setSelectedCollectionId(e.target.value)}
            displayEmpty
            sx={{
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: primaryColor,
                },
                '&.Mui-focused fieldset': {
                  borderColor: primaryColor,
                },
              },
            }}
          >
            <MenuItem value="" disabled>
              {t('collection.select_collection') || 'Select a collection...'}
            </MenuItem>
            {collections.map((collection) => (
              <MenuItem key={collection.id} value={collection.id}>
                {collection.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {collections.length === 0 && (
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 2, 
              color: 'text.secondary',
              fontStyle: 'italic'
            }}
          >
            {t('collection.no_collections_available') || 'No collections available. Create a collection first.'}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={handleCancel}
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: lightPurple,
            }
          }}
        >
          {t('common.cancel') || 'Cancel'}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedCollectionId}
          sx={{
            backgroundColor: primaryColor,
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: primaryColor,
              opacity: 0.9,
            },
            '&:disabled': {
              backgroundColor: '#ccc',
            }
          }}
        >
          {t('collection.add_to_collection') || 'Add to Collection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToCollectionDialog;